import { useState, useCallback, useEffect } from "react";

interface UseYearWindowOptions {
  fetchSummary: (year: number) => void;
}

export function useYearWindow(
  summary: any[],
  initialWindowStartIdx = 0,
  initialSelectedYear?: number,
  windowSize = 5,
  options?: UseYearWindowOptions,
  minYear = 1940
) {
  const currentYear = new Date().getFullYear();
  // Track window start and selected year independently
  const [windowStart, setWindowStart] = useState(
    initialSelectedYear
      ? initialSelectedYear - (windowSize - 1)
      : currentYear - (windowSize - 1)
  );
  const [selectedYear, setSelectedYear] = useState(
    initialSelectedYear || currentYear
  );
  const visibleYears = Array.from(
    { length: windowSize },
    (_, i) => windowStart + i
  );
  const showNext = visibleYears[windowSize - 1] < currentYear;
  const showPrev = windowStart > minYear;

  // Only call fetchSummary when explicitly triggered (prev/next)
  const triggerFetchSummary = (year: number) => {
    if (options?.fetchSummary) {
      options.fetchSummary(year);
    }
  };

  const handlePrev = useCallback(() => {
    setWindowStart((prev) => {
      const newStart = Math.max(minYear, prev - windowSize);
      triggerFetchSummary(newStart + windowSize - 1);
      return newStart;
    });
    setSelectedYear((prev) =>
      Math.max(minYear + windowSize - 1, prev - windowSize)
    );
  }, [windowSize]);

  const handleNext = useCallback(() => {
    setWindowStart((prev) => {
      const newStart = Math.min(
        currentYear - windowSize + 1,
        prev + windowSize
      );
      triggerFetchSummary(newStart + windowSize - 1);
      return newStart;
    });
    setSelectedYear((prev) => Math.min(currentYear, prev + windowSize));
  }, [windowSize, currentYear]);

  return {
    visibleYears,
    selectedYear,
    setSelectedYear,
    handlePrev,
    handleNext,
    showPrev,
    showNext,
    windowSize,
  };
}
